import { join } from 'path';
import type { NativeScanOptions, NativeScanResult, MacOSScanOptions, MacOSScanResult } from './types.js';
import type { ScanIssue } from '../scan.js';
import { aggregateIssues, determineVerdict, generateSummary, applyDesignSystemCheck } from '../scan.js';
import { analyzeElements } from '../extract.js';
import { findDevice, getBootedDevices, bootDevice } from './simulator.js';
import { captureNativeScreenshot } from './capture.js';
import { getDeviceViewport } from './viewports.js';
import { extractNativeElements, mapToEnhancedElements, isExtractorAvailable } from './extract.js';
import { auditNativeElements } from './rules.js';
import { findProcess, extractMacOSElements, mapMacOSToEnhancedElements, captureMacOSScreenshot } from './macos.js';
import { buildNativeInteractivity } from './interactivity.js';
import { buildNativeSemantic } from './semantic.js';
import { analyzeLayoutFill, type LayoutFillFinding } from './layout-fill.js';
import type { AuditResult, EnhancedElement } from '../schemas.js';

/**
 * Run a comprehensive native simulator scan
 *
 * Orchestrates: device resolution → boot → screenshot → element extraction → audit → verdict
 *
 * Falls back to screenshot-only mode if the Swift AXUIElement extractor is unavailable.
 */
export async function scanNative(options: NativeScanOptions = {}): Promise<NativeScanResult> {
  const { device: deviceQuery, screenshot = true, outputDir = '.ibr' } = options;

  // --- Resolve device ---
  let device;
  if (deviceQuery) {
    device = await findDevice(deviceQuery);
    if (!device) {
      throw new Error(
        `No simulator found matching "${deviceQuery}". ` +
        'Run `xcrun simctl list devices available` to see available simulators.'
      );
    }
  } else {
    // Use first booted device
    const booted = await getBootedDevices();
    if (booted.length === 0) {
      throw new Error(
        'No booted simulators found. Boot one with: xcrun simctl boot <device-name>'
      );
    }
    device = booted[0];
  }

  // --- Boot if needed ---
  if (device.state !== 'Booted') {
    await bootDevice(device.udid);
    // Refresh device state
    const refreshed = await findDevice(device.udid);
    if (refreshed) device = refreshed;
  }

  const viewport = getDeviceViewport(device);
  const url = `simulator://${device.name}/${options.bundleId || 'current'}`;

  // --- Capture screenshot ---
  let screenshotPath: string | undefined;
  if (screenshot) {
    const timestamp = Date.now();
    const ssPath = join(outputDir, 'native', `${device.udid.slice(0, 8)}-${timestamp}.png`);
    const captureResult = await captureNativeScreenshot({
      device,
      outputPath: ssPath,
    });
    if (captureResult.success) {
      screenshotPath = captureResult.outputPath;
    }
  }

  // --- Extract elements ---
  let elements: EnhancedElement[] = [];
  let audit: AuditResult = {
    totalElements: 0,
    interactiveCount: 0,
    withHandlers: 0,
    withoutHandlers: 0,
    issues: [],
  };
  let extractionSucceeded = false;

  // On modern Xcode (12+), iOS guest accessibility is no longer bridged into
  // the macOS host process — only Simulator.app's chrome is visible. Filter
  // out the chrome roles below so verdicts don't rate menu bar items as SUT
  // content. The native AX→tagName mapping yields these for host chrome:
  //   AXApplication → application
  //   AXMenuBar / AXMenu → nav
  //   AXMenuBarItem / AXMenuItem → menubaritem / li
  const HOST_CHROME_TAGS = new Set([
    'application',
    'menubaritem',
    'nav',
  ]);

  let iosGuestUnreachable = false;

  if (isExtractorAvailable()) {
    try {
      const nativeElements = await extractNativeElements(device);
      const allMapped = mapToEnhancedElements(nativeElements);
      // Drop host-chrome roles for iOS targets. On macOS targets the same
      // filter is a no-op because the AX root is the app itself, not the host.
      const filtered =
        device.platform === 'ios'
          ? allMapped.filter((e) => !HOST_CHROME_TAGS.has((e.tagName || '').toLowerCase()))
          : allMapped;
      elements = filtered;
      // If we got results from the AX query but everything was host chrome,
      // the iOS guest tree is unreachable from this process. Surface it.
      if (device.platform === 'ios' && allMapped.length > 0 && filtered.length === 0) {
        iosGuestUnreachable = true;
      }
      audit = analyzeElements(elements, true); // Always treat as mobile-sized targets
      extractionSucceeded = !iosGuestUnreachable;
    } catch {
      // Graceful fallback to screenshot-only mode
    }
  }

  // --- Native audit rules ---
  const nativeIssues = extractionSucceeded
    ? auditNativeElements(elements, device.platform, viewport)
    : [];

  // --- Aggregate issues ---
  // Build ScanIssue array from audit issues and native-specific issues
  const issues: ScanIssue[] = [];

  // Standard audit issues
  for (const issue of audit.issues) {
    issues.push({
      category: issue.type === 'MISSING_ARIA_LABEL' ? 'accessibility' : 'interactivity',
      severity: issue.severity,
      description: issue.message,
    });
  }

  // Native-specific issues
  for (const issue of nativeIssues) {
    issues.push({
      category: issue.type === 'MISSING_ARIA_LABEL' ? 'accessibility' : 'structure',
      severity: issue.severity,
      description: issue.message,
    });
  }

  // Honest signal: iOS guest accessibility tree is not reachable from the
  // macOS host process on modern Xcode. The scan returned host chrome only.
  if (iosGuestUnreachable) {
    issues.push({
      category: 'structure',
      severity: 'error',
      description:
        'iOS guest accessibility tree is unreachable from this process. ' +
        'native:scan can only see Simulator.app chrome on Xcode 12+. ' +
        'Use a screenshot-driven workflow, or install IDB and route through ' +
        '`idb accessibility info` (planned: XCUITest bundle, see NATIVE_SUPPORT_PROPOSAL.md Phase 2).',
    });
  }

  // --- Design system check (only when explicitly provided, not the default '.ibr') ---
  const designSystem = options.outputDir ? await applyDesignSystemCheck(
    elements, issues, viewport, url, options.outputDir
  ) : undefined;

  // --- Verdict ---
  const verdict = determineVerdict(issues);

  // --- Summary ---
  const summary = generateNativeSummary(device, elements, issues, extractionSucceeded);

  return {
    url,
    route: `/${device.name}`,
    timestamp: new Date().toISOString(),
    viewport,
    platform: device.platform,
    device: {
      name: device.name,
      udid: device.udid,
      runtime: device.runtime,
    },
    elements: { all: elements, audit },
    nativeIssues,
    screenshotPath,
    designSystem,
    verdict,
    issues,
    summary,
  };
}

/**
 * Generate human-readable summary for native scan
 */
function generateNativeSummary(
  device: { name: string; platform: string },
  elements: EnhancedElement[],
  issues: ScanIssue[],
  extractionSucceeded: boolean
): string {
  const parts: string[] = [];

  parts.push(`${device.platform} simulator (${device.name})`);

  if (extractionSucceeded) {
    const interactive = elements.filter(e => e.interactive.hasOnClick).length;
    parts.push(`${elements.length} elements (${interactive} interactive)`);
  } else {
    parts.push('screenshot-only mode (element extraction unavailable)');
  }

  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  if (errorCount > 0 || warningCount > 0) {
    const issueParts = [];
    if (errorCount > 0) issueParts.push(`${errorCount} errors`);
    if (warningCount > 0) issueParts.push(`${warningCount} warnings`);
    parts.push(issueParts.join(', '));
  }

  return parts.join(', ');
}

// ============================================================================
// macOS Native App Scanning
// ============================================================================

/**
 * Run a comprehensive scan of a running macOS native app
 *
 * Uses the Accessibility API (AXUIElement) to extract the full view hierarchy,
 * then runs the same analysis pipeline as web scans.
 *
 * Produces a MacOSScanResult with the same shape as web ScanResult.
 */
export async function scanMacOS(options: MacOSScanOptions): Promise<MacOSScanResult> {
  if (process.platform !== 'darwin') {
    throw new Error('macOS native scanning is only available on macOS');
  }

  const { app, bundleId, pid: directPid, screenshot } = options;

  if (!app && !bundleId && !directPid) {
    throw new Error('Provide --app, --bundle-id, or --pid to identify the target app');
  }

  // --- Resolve PID ---
  let pid: number;
  if (directPid) {
    pid = directPid;
  } else {
    pid = await findProcess(app || bundleId!);
  }

  // --- Extract elements via Swift CLI ---
  const { elements: nativeElements, window } = await extractMacOSElements({
    pid,
    app: app || bundleId,
  });

  // --- Map to EnhancedElement[] ---
  const elements = mapMacOSToEnhancedElements(nativeElements);

  // --- Analyze elements (reuse from extract.ts) ---
  const audit = analyzeElements(elements, false); // desktop-sized targets

  // --- Build interactivity result from AX data ---
  const interactivity = buildNativeInteractivity(elements);

  // --- Build semantic result from element composition ---
  const semantic = buildNativeSemantic(elements, window);

  // --- Layout-fill / gap analysis ---
  // Reads the raw AX tree (NOT the flattened EnhancedElement list) so it sees
  // the parent → child structure required to compute per-container gaps.
  // Catches centered-narrow-content bugs that pass screenshot/a11y checks.
  let layoutFill: LayoutFillFinding[] | undefined;
  if (options.layoutFill !== false) {
    const lfOpts = typeof options.layoutFill === 'object' ? options.layoutFill : {};
    layoutFill = analyzeLayoutFill(nativeElements, {
      threshold: lfOpts.threshold,
      minContainerPx: lfOpts.minContainerPx,
      window: { width: window.width, height: window.height },
    });
  }

  // --- Capture screenshot if requested ---
  if (screenshot && window.windowId > 0) {
    await captureMacOSScreenshot(window.windowId, screenshot.path);
  }

  // --- Build URL ---
  const url = `macos://${app || bundleId || `pid-${pid}`}/${window.title}`;
  const route = `/${window.title}`;

  // --- Build viewport from window dimensions ---
  const viewport = {
    name: 'native',
    width: window.width,
    height: window.height,
  };

  // --- Aggregate issues (reuse from scan.ts) ---
  const issues = aggregateIssues(audit, interactivity, semantic, []);

  // --- Surface layout-fill findings as WARNING issues ---
  // Same shape as touch-target warnings so consumers (CLI, MCP, dashboard)
  // handle them uniformly. Category "layout" is the right home (not
  // "accessibility" — the band is a layout signal, not an a11y one).
  if (layoutFill) {
    for (const f of layoutFill) {
      issues.push({
        category: 'structure',
        severity: 'warning',
        description: `layout-fill: ${f.detail}`,
      });
    }
  }

  // --- Design system check ---
  const designSystem = options.outputDir ? await applyDesignSystemCheck(
    elements, issues, viewport, url, options.outputDir
  ) : undefined;

  // --- Verdict ---
  const verdict = determineVerdict(issues);

  // --- Generate summary (reuse from scan.ts) ---
  const summary = generateSummary(
    { all: elements, audit },
    interactivity,
    semantic,
    issues,
    []
  );

  return {
    url,
    route,
    timestamp: new Date().toISOString(),
    viewport,
    elements: { all: elements, audit },
    interactivity,
    semantic,
    console: { errors: [], warnings: [] },
    designSystem,
    verdict,
    issues,
    summary,
    layoutFill,
  };
}

// --- Shared formatter helpers ---

function verdictIcon(verdict: string): string {
  return verdict === 'PASS' ? '\x1b[32m✓\x1b[0m' :
         verdict === 'ISSUES' ? '\x1b[33m!\x1b[0m' :
         '\x1b[31m✗\x1b[0m';
}

function formatElementsSection(audit: AuditResult): string[] {
  return [
    '  ELEMENTS',
    '  ────────',
    `  Total:              ${audit.totalElements}`,
    `  Interactive:        ${audit.interactiveCount}`,
    `  With handlers:      ${audit.withHandlers}`,
    `  Without handlers:   ${audit.withoutHandlers}`,
    '',
  ];
}

function formatIssuesSection(issues: ScanIssue[]): string[] {
  if (issues.length === 0) return ['  No issues detected.'];
  const lines = ['  ISSUES', '  ──────'];
  for (const issue of issues) {
    const icon = issue.severity === 'error' ? '\x1b[31m✗\x1b[0m' :
                 issue.severity === 'warning' ? '\x1b[33m!\x1b[0m' : 'ℹ';
    lines.push(`  ${icon} [${issue.category}] ${issue.description}`);
    if (issue.fix) lines.push(`    → ${issue.fix}`);
  }
  return lines;
}

/**
 * Format macOS scan result for console output
 */
export function formatMacOSScanResult(result: MacOSScanResult): string {
  const lines: string[] = [
    '═══════════════════════════════════════════════════════',
    '  IBR NATIVE macOS SCAN',
    '═══════════════════════════════════════════════════════',
    '',
    `  App:      ${result.url}`,
    `  Window:   ${result.route.slice(1)}`,
    `  Viewport: ${result.viewport.width}x${result.viewport.height}`,
    `  Verdict:  ${verdictIcon(result.verdict)} ${result.verdict}`,
    '',
    `  ${result.summary}`,
    '',
    '  PAGE UNDERSTANDING',
    '  ─────────────────',
    `  Intent:   ${result.semantic.pageIntent.intent} (${Math.round(result.semantic.confidence * 100)}% confidence)`,
    `  Auth:     ${result.semantic.state.auth.authenticated === false ? 'Not authenticated' : result.semantic.state.auth.authenticated ? 'Authenticated' : 'Unknown'}`,
    '',
    ...formatElementsSection(result.elements.audit),
  ];

  const { buttons, links, forms } = result.interactivity;
  lines.push('  INTERACTIVITY', '  ─────────────');
  lines.push(`  Buttons: ${buttons.length}  Links: ${links.length}  Forms: ${forms.length}`, '');
  lines.push(...formatIssuesSection(result.issues));
  lines.push('', '═══════════════════════════════════════════════════════');

  return lines.join('\n');
}

/**
 * Format native scan result for console output
 */
export function formatNativeScanResult(result: NativeScanResult): string {
  const lines: string[] = [
    '═══════════════════════════════════════════════════════',
    '  IBR NATIVE SCAN',
    '═══════════════════════════════════════════════════════',
    '',
    `  Device:   ${result.device.name}`,
    `  Platform: ${result.platform}`,
    `  Runtime:  ${result.device.runtime.replace(/^.*SimRuntime\./, '').replace(/-/g, '.')}`,
    `  Viewport: ${result.viewport.name} (${result.viewport.width}x${result.viewport.height})`,
    `  Verdict:  ${verdictIcon(result.verdict)} ${result.verdict}`,
    '',
    `  ${result.summary}`,
    '',
    ...formatElementsSection(result.elements.audit),
  ];

  if (result.screenshotPath) {
    lines.push(`  Screenshot: ${result.screenshotPath}`, '');
  }

  lines.push(...formatIssuesSection(result.issues));
  lines.push('', '═══════════════════════════════════════════════════════');

  return lines.join('\n');
}
